import { ImSys } from '../core/ImSys.js';
import { Widget } from '../core/Widget.js';
import { Loc } from '../locales/registry.js';
import { ViewWidgetHelper } from '../view/ViewWidgetHelper.js';

// tabs/TabsPanelWidget.ts
/** Ключ виджета */
const TABS_PANEL_WIDGET_KEY = "TabsPanel";
/**
 * Панельные вкладки (порт старого $.imPanel на TypeScript).
 *
 * Разметка:
 * ```html
 * <div class="im-panel">
 *   <div class="im-cmdpanel">
 *     <span class="im-tab current" data-id="0">...</span>
 *   </div>
 *   <div class="im-panelcontent">
 *     <div class="im-tabcontent current" data-id="0">...</div>
 *   </div>
 * </div>
 * ```
 */
class TabsPanelWidget extends Widget {
    constructor() {
        super(...arguments);
        this._uiNotMask = '';
        this.contentNumerator = 0;
        this.tabOrder = [];
        /** Клик по вкладке */
        this.onTabClick = (tab) => {
            this.tabPrev = tab;
            const tabIndex = tab.dataset.id ?? "";
            // ✅ запомним предыдущую активную панель ДО переключения
            const oldPanel = this._content.querySelector(".im-tabcontent.current") || null;
            // активируем таб
            this._panel.querySelector(".current")?.classList.remove("current");
            tab.classList.add("current");
            // скрыть все контенты
            this._content.querySelector(".current")?.classList.remove("current");
            this._content.querySelectorAll(".im-tabcontent").forEach(c => {
                c.style.display = "none";
                c.setAttribute("hidden", "");
            });
            // показать контент активной вкладки
            const cont = this.getTabContent(tabIndex);
            if (cont) {
                cont.classList.add("current");
                cont.style.display = "";
                cont.removeAttribute("hidden");
            }
            // порядок
            const idx = this.tabOrder.indexOf(tabIndex);
            if (idx !== -1)
                this.tabOrder.splice(idx, 1);
            this.tabOrder.push(tabIndex);
            // ✅ отдаём и oldPanel, и newPanel
            this._emitTabsActivate(cont || null, oldPanel);
        };
        this._onTabClickDom = (e) => {
            const target = e.currentTarget;
            if (!target)
                return;
            if (e.target?.closest(".bt-close"))
                return;
            // уважаем ui_notmask из view (как при инициализации через view)
            if (!this._tabAllowed(target))
                return;
            this.onTabClick(target);
        };
        this._onCloseClick = (e) => {
            e.stopPropagation();
            const btn = e.currentTarget;
            const tab = btn?.closest(".im-tab");
            if (!tab)
                return;
            const id = tab.dataset.id ?? "";
            this.closeTab(id, true);
        };
        this._onTabClick = this._onTabClickDom;
    }
    /** Ключевой класс для поиска виджета */
    static _cssKey() {
        return 'im-panel';
    }
    /** Доступ к DOM-панели вкладок */
    panel() { return this._panel; }
    /** Доступ к DOM-контенту вкладок */
    content() { return this._content; }
    /** Найти вкладку по data-id */
    getTab(tabID) {
        return this._panel.querySelector(`.im-tab[data-id="${tabID}"]`);
    }
    /** Найти контент по data-id */
    getTabContent(tabID) {
        return this._content.querySelector(`.im-tabcontent[data-id="${tabID}"]`);
    }
    /** Добавить вкладку */
    addTab(data) {
        if (data.nodeKey !== undefined) {
            const exist = this._panel.querySelector(`.im-tab[data-ownerkey="${data.nodeKey}"]`);
            if (exist) {
                this.onTabClick(exist);
                return null;
            }
        }
        const num = this.contentNumerator++;
        const idStr = String(num);
        // --- вкладка
        const tab = document.createElement("span");
        tab.className = "im-tab current";
        tab.dataset.ownerkey = data.nodeKey ?? "";
        tab.dataset.id = idStr;
        tab.title = data.description ?? "";
        const textSpan = document.createElement("span");
        textSpan.className = "im-tab__text";
        textSpan.innerHTML = data.title;
        tab.appendChild(textSpan);
        if (data.protect === undefined) {
            const close = document.createElement("i");
            close.className = "bt-close";
            tab.appendChild(close);
        }
        if (data.imgElement)
            tab.prepend(data.imgElement);
        // --- контент
        const content = document.createElement("div");
        content.className = "im-tabcontent current";
        content.dataset.id = idStr;
        if (data.viewChanges === true)
            content.dataset.viewchanges = "True";
        if (data.element) {
            content.appendChild(data.element);
        }
        else if (data.html !== undefined) {
            content.innerHTML = data.html;
            ImSys.initDom(content);
        }
        // снять current и скрыть предыдущие
        this._panel.querySelector(`.im-tab.current${this._uiNotMask}`)?.classList.remove("current");
        const prev = this._content.querySelector(".im-tabcontent.current");
        if (prev) {
            prev.classList.remove("current");
            prev.style.display = "none";
            prev.setAttribute("hidden", "");
        }
        // добавить новые элементы
        this._panel.append(tab);
        this._content.append(content);
        ViewWidgetHelper.closestOnByName(this.element)?.dom.setXClass();
        // показать новый контент
        content.style.display = "";
        content.removeAttribute("hidden");
        // события
        tab.addEventListener("click", this._onTabClick);
        const closeBtn = tab.querySelector(".bt-close");
        if (closeBtn)
            closeBtn.addEventListener("click", this._onCloseClick);
        this.tabOrder.push(idStr);
        return num;
    }
    /** Заменить html контента */
    setTabContent(tabID, html) {
        const content = this.getTabContent(tabID);
        if (!content)
            return null;
        content.innerHTML = html;
        ImSys.initDom(content);
        return content;
    }
    /** Закрыть вкладку */
    closeTab(tabID, checkChanges) {
        const tab = this.getTab(tabID);
        const cont = this.getTabContent(tabID);
        if (!tab || !cont)
            return;
        if (checkChanges && tab.querySelector(".changeMarker")) {
            if (!window.confirm(Loc.closeUnsavedTabConfirmation))
                return;
        }
        const wasActive = tab.classList.contains("current");
        // отписки
        tab.removeEventListener("click", this._onTabClick);
        tab.querySelector(".bt-close")?.removeEventListener("click", this._onCloseClick);
        // удалить DOM
        tab.remove();
        cont.remove();
        // порядок
        const idStr = String(tabID);
        const idx = this.tabOrder.indexOf(idStr);
        if (idx !== -1)
            this.tabOrder.splice(idx, 1);
        if (wasActive) {
            // выбрать новую активную: tabPrev → последняя в порядке → первая
            let newTab = null;
            if (this.tabPrev && this.element.contains(this.tabPrev)) {
                newTab = this.tabPrev;
            }
            else if (this.tabOrder.length > 0) {
                const lastId = this.tabOrder[this.tabOrder.length - 1];
                newTab = this.getTab(lastId);
            }
            else {
                newTab = this._panel.querySelector(".im-tab:first-child");
            }
            if (newTab) {
                this.onTabClick(newTab);
            }
        }
    }
    /** Активировать вкладку по индексу */
    setActive(index) {
        const list = this._panel.querySelectorAll(`.im-tab${this._uiNotMask}`);
        if (!list.length)
            return;
        const i = Math.max(0, Math.min(index, list.length - 1));
        const tab = list[i] || null;
        if (tab)
            this.onTabClick(tab);
    }
    /** Активировать по data-id */
    setActiveByPanelId(panelId) {
        const tab = this._panel.querySelector(`.im-tab${this._uiNotMask}[data-id="${panelId}"]`)
            ?? this.getTab(panelId);
        if (tab && this._tabAllowed(tab))
            this.onTabClick(tab);
    }
    /** Инициализация */
    _create(_options) {
        this._uiNotMask = ViewWidgetHelper.closestOnByName(this.element)?.getUiNotMask() || '';
        this._panel = this._ensureChild(this.element, "div", "im-cmdpanel");
        this._content = this._ensureChild(this.element, "div", "im-panelcontent");
        const tabs = Array.from(this._panel.querySelectorAll(`.im-tab${this._uiNotMask}`));
        for (const tab of tabs) {
            tab.addEventListener("click", this._onTabClick);
            const close = tab.querySelector(".bt-close");
            if (close)
                close.addEventListener("click", this._onCloseClick);
            const numStr = tab.dataset.id ?? "0";
            const num = parseInt(numStr, 10) || 0;
            this.contentNumerator = Math.max(num + 1, this.contentNumerator + 1);
            this.tabOrder.push(numStr);
        }
        this.tabPrev = this._panel.querySelector(".im-tab.current") ?? undefined;
    }
    // + маленький хелпер: можно ли обрабатывать эту вкладку с учётом ui_notmask
    _tabAllowed(tab) {
        return !this._uiNotMask || tab.matches(`.im-tab${this._uiNotMask}`);
    }
    _ensureChild(parent, tag, className) {
        let el = parent.querySelector(`.${className}`);
        if (!el) {
            el = document.createElement(tag);
            el.className = className;
            if (className === "im-cmdpanel")
                parent.prepend(el);
            else
                parent.append(el);
        }
        return el;
    }
    /** События активации вкладки */
    _emitTabsActivate(newPanel, oldPanel) {
        this.element.dispatchEvent(new Event("tabClicked")); // старое событие
        this.element.dispatchEvent(new CustomEvent("tabsactivate", { detail: { newPanel, oldPanel } }));
        if (this.element.hasAttribute("data-onchange") || this.element.hasAttribute("data-imonchange")) {
            this.element.dispatchEvent(new Event("change", { bubbles: false }));
        }
    }
    /** Поставить маркер изменений по content-элементу или data-id */
    setChangeMarker(target) {
        const id = typeof target === 'object'
            ? (target.closest('.im-tabcontent')?.dataset.id ?? '')
            : String(target);
        if (!id)
            return;
        const tab = this.getTab(id);
        if (!tab)
            return;
        // уже есть
        if (tab.querySelector('.changeMarker'))
            return;
        // Проверяем есть ли кнопка закрыть
        const closeBtn = tab.querySelector('.bt-close');
        if (!closeBtn?.parentElement)
            return;
        // Если есть, устанавливаем маркер
        const mark = document.createElement('b');
        mark.className = 'changeMarker';
        mark.textContent = '*';
        closeBtn.parentElement.insertBefore(mark, closeBtn);
    }
    /** Снять маркер изменений по content-элементу или data-id */
    clearChangeMarker(target) {
        const id = typeof target === 'object'
            ? (target.closest('.im-tabcontent')?.dataset.id ?? '')
            : String(target);
        if (!id)
            return;
        const tab = this.getTab(id);
        if (!tab)
            return;
        tab.querySelector('.changeMarker')?.remove();
    }
    /** Проверка - можно ли закрыть окно */
    canClose() {
        // если есть хотя бы один маркер изменений → блокируем закрытие
        const hasMarker = !!this.element.querySelector('.im-tab .changeMarker');
        if (!hasMarker)
            return null;
        return Loc.pageContainsUnsavedData;
    }
    /** Очистка ресурсов */
    _onDestroy() {
        const tabs = Array.from(this._panel?.querySelectorAll(".im-tab") ?? []);
        for (const tab of tabs) {
            tab.removeEventListener("click", this._onTabClick);
            const closeBtn = tab.querySelector(".bt-close");
            if (closeBtn)
                closeBtn.removeEventListener("click", this._onCloseClick);
        }
        this.tabOrder = [];
        this.tabPrev = undefined;
    }
}
/** Имя виджета */
TabsPanelWidget._alias = "TabsPanel";

export { TABS_PANEL_WIDGET_KEY, TabsPanelWidget };
